// src/components/GoogleMap/GoogleMap.js
import React, { useEffect, useState } from 'react'
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet'
import { Box, Typography, CircularProgress } from '@mui/material'
import { LocationOn } from '@mui/icons-material'
import L from 'leaflet'
import 'leaflet/dist/leaflet.css'

// Fix para ícones do Leaflet no React
delete L.Icon.Default.prototype._getIconUrl
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
  iconUrl: require('leaflet/dist/images/marker-icon.png'),
  shadowUrl: require('leaflet/dist/images/marker-shadow.png')
})

const GoogleMap = ({ latitude, longitude, zipcode, street, number, complement, neighborhood, city, state }) => {
  const [position, setPosition] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  useEffect(() => {
    // Se já temos latitude e longitude, usar diretamente
    if (latitude && longitude) {
      setPosition([parseFloat(latitude), parseFloat(longitude)])
      setLoading(false)
      return
    }

    // Caso contrário, buscar por CEP ou endereço
    if (!zipcode && !street) {
      setError('Endereço não disponível')
      setLoading(false)
      return
    }

    geocodeAddress()
  }, [latitude, longitude, zipcode, street, city])

  const geocodeAddress = async () => {
    try {
      // Construir endereço completo para busca mais precisa
      let searchQuery = ''

      if (street && city && state) {
        // Usar endereço completo se disponível
        searchQuery = `${street}${number ? ', ' + number : ''}, ${neighborhood ? neighborhood + ', ' : ''}${city}, ${state}, Brasil`
      } else if (zipcode) {
        // Fallback para CEP
        searchQuery = `${zipcode}, Brasil`
      } else {
        setError('Informações insuficientes para localizar no mapa')
        setLoading(false)
        return
      }

      // Usar Nominatim (serviço de geocodificação do OpenStreetMap)
      const response = await fetch(
        `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(
          searchQuery
        )}&limit=1`
      )

      const data = await response.json()

      if (data && data.length > 0) {
        const { lat, lon } = data[0]
        setPosition([parseFloat(lat), parseFloat(lon)])
        setLoading(false)
      } else {
        setError('Não foi possível localizar o endereço no mapa')
        setLoading(false)
      }
    } catch (err) {
      console.error('Erro ao geocodificar endereço:', err)
      setError('Erro ao carregar o mapa')
      setLoading(false)
    }
  }

  if (loading) {
    return (
      <Box
        sx={{
          height: 400,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: 'grey.100',
          borderRadius: 1
        }}
      >
        <CircularProgress />
      </Box>
    )
  }

  if (error || !position) {
    return (
      <Box
        sx={{
          height: 400,
          bgcolor: 'grey.100',
          border: '2px dashed',
          borderColor: 'grey.300',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          borderRadius: 1
        }}
      >
        <LocationOn sx={{ fontSize: 48, color: 'grey.400', mb: 1 }} />
        <Typography color='text.secondary'>{error}</Typography>
      </Box>
    )
  }

  return (
    <Box
      sx={{
        height: 400,
        borderRadius: 1,
        overflow: 'hidden',
        '& .leaflet-container': {
          height: '100%',
          width: '100%',
          borderRadius: 1
        }
      }}
    >
      <MapContainer
        center={position}
        zoom={16}
        scrollWheelZoom={false}
        style={{ height: '100%', width: '100%' }}
      >
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
          url='https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png'
        />
        <Marker position={position}>
          <Popup>
            <Box sx={{ p: 1 }}>
              <Typography variant='body2' fontWeight='bold' sx={{ mb: 0.5 }}>
                📍 Localização do Imóvel
              </Typography>
              {street && (
                <Typography variant='caption' display='block'>
                  {street}{number && `, ${number}`}
                </Typography>
              )}
              {complement && (
                <Typography variant='caption' display='block'>
                  {complement}
                </Typography>
              )}
              {neighborhood && (
                <Typography variant='caption' display='block'>
                  {neighborhood}
                </Typography>
              )}
              {city && state && (
                <Typography variant='caption' display='block' sx={{ fontWeight: 600 }}>
                  {city} - {state}
                </Typography>
              )}
              {zipcode && (
                <Typography variant='caption' display='block' color='text.secondary'>
                  CEP: {zipcode}
                </Typography>
              )}
            </Box>
          </Popup>
        </Marker>
      </MapContainer>
    </Box>
  )
}

export default GoogleMap
